import React, { LegacyRef, useEffect, useRef, useState } from 'react'

import { useSelector } from 'react-redux'

import {
  Avatar,
  Box,
  Flex,
  Popover,
  PopoverContent,
  Portal,
  useDisclosure,
  useTheme,
} from '@chakra-ui/react'

import { PopoverTrigger } from '@/components/ui'
import { getRoleDisplayName } from '@/features/managed-accounts'
import { selectFullName, selectRole } from '@/redux/me/meSlice'
import { BUTTON_PRESS, mixpanel } from '@/utils/analytics'

import { ProfileMenuActions } from './ProfileMenuActions'
import { SideBarProfileCard } from './SideBarProfileCard'
import { PROFILE_POPOVER_STYLES } from './constants'

export const SideBarProfile = () => {
  const theme = useTheme()
  const fullName = useSelector(selectFullName)
  const role = useSelector(selectRole)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [isHelpPopoverOpen, setIsHelpPopoverOpen] = useState(false)
  const [helpPopoverContentRef, setHelpPopoverContentRef] =
    useState<HTMLDivElement | null>(null)
  const popoverContentRef = useRef<HTMLDivElement>(null)

  const roleDisplayName = getRoleDisplayName(role)

  // Handle clicks outside both popovers
  useEffect(() => {
    if (!isOpen && !isHelpPopoverOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      // Check if click is outside both popovers
      const isClickedOutsideProfile =
        popoverContentRef.current && !popoverContentRef.current.contains(target)
      const isClickedOutsideHelp =
        !helpPopoverContentRef || !helpPopoverContentRef.contains(target)

      if (isClickedOutsideProfile && isClickedOutsideHelp) {
        onClose()
      }
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, PROFILE_POPOVER_STYLES.CLICK_OUTSIDE_DELAY)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, isHelpPopoverOpen, helpPopoverContentRef, onClose])

  const handleProfileCardClick = () => {
    mixpanel.track(`${BUTTON_PRESS} Profile Card`)
  }

  const handlePopoverClose = () => {
    mixpanel.track('Closed Profile Popover')
    onClose()
  }

  const handlePopoverOpen = () => {
    mixpanel.track('Opened Profile Popover')
    onOpen()
  }

  return (
    <Popover
      closeOnBlur={false}
      closeOnEsc={true}
      isOpen={isOpen}
      onClose={handlePopoverClose}
      onOpen={handlePopoverOpen}
      placement='right'
    >
      <PopoverTrigger>
        <Box onClick={handleProfileCardClick}>
          <SideBarProfileCard />
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          borderRadius={PROFILE_POPOVER_STYLES.BORDER_RADIUS}
          data-testid='SideBarProfileMenu:popover'
          mb={PROFILE_POPOVER_STYLES.MARGIN_BOTTOM}
          py={PROFILE_POPOVER_STYLES.PADDING_Y}
          ref={popoverContentRef as LegacyRef<any>}
          w={PROFILE_POPOVER_STYLES.WIDTH}
        >
          {/* Profile Header Section */}
          <Box px={PROFILE_POPOVER_STYLES.HEADER_PADDING_X}>
            <Flex alignItems='center' gap={PROFILE_POPOVER_STYLES.HEADER_GAP}>
              <Avatar
                bg={theme.colors.sidebar.avatarBg as string}
                color='white'
                h={PROFILE_POPOVER_STYLES.AVATAR_SIZE}
                name={fullName}
                sx={{
                  '.chakra-avatar__initials': {
                    fontSize: PROFILE_POPOVER_STYLES.AVATAR_FONT_SIZE,
                    fontWeight: PROFILE_POPOVER_STYLES.AVATAR_FONT_WEIGHT,
                  },
                }}
                w={PROFILE_POPOVER_STYLES.AVATAR_SIZE}
              />
              <Box>
                <Box
                  color={theme.colors.sidebar.darkText}
                  fontSize={PROFILE_POPOVER_STYLES.NAME_FONT_SIZE}
                  fontWeight='semibold'
                  lineHeight='shorter'
                  noOfLines={1}
                >
                  {fullName}
                </Box>
                <Box
                  color='gray.500'
                  fontSize={PROFILE_POPOVER_STYLES.ROLE_FONT_SIZE}
                  fontWeight='normal'
                  lineHeight='shorter'
                  mt={PROFILE_POPOVER_STYLES.ROLE_MARGIN_TOP}
                >
                  {roleDisplayName}
                </Box>
              </Box>
            </Flex>
          </Box>

          {/* Actions Section */}
          <ProfileMenuActions
            isProfilePopoverOpen={isOpen}
            onHelpPopoverOpenChange={setIsHelpPopoverOpen}
            onHelpPopoverRefChange={setHelpPopoverContentRef}
            profilePopoverRef={popoverContentRef}
          />
        </PopoverContent>
      </Portal>
    </Popover>
  )
}
